import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import FileUploader from 'devextreme-react/file-uploader';
import { DialogActions } from '@material-ui/core';

interface FileSelectDialogProps {
    options: {
        ownerId?: String;
        isOpen: Boolean;
        fileTypeName?: String;
        maxFiles: number;
    };
    onSubmit: (ownerId: any, currentFiles: any) => void;
    onCancel: () => void;
}

export const FileSelectDialog = ({
    options = {
        ownerId: undefined,
        isOpen: false,
        fileTypeName: undefined,
        maxFiles: 1,
    },
    onSubmit,
    onCancel,
}: FileSelectDialogProps) => {
    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: { paddingBottom: '0', paddingLeft: '10px' },
        fileUpload: {
            display: 'inline-block',
            padding: '10px',
            cursor: 'pointer',
        },
        dropzone: {
            width: '100%',
            backgroundColor: 'silver',
            padding: '10px',
        },
    };

    const maxFileSize = 1024 * 1024 * 35;
    const [currentFiles, set_currentFiles] = useState<File[]>([]);
    const [canContinue, set_canContinue] = useState(false);

    useEffect(() => {
        if (!options) {
            options = {
                ownerId: undefined,
                isOpen: false,
                fileTypeName: undefined,
                maxFiles: 1,
            };
        }
        if (options.maxFiles < 1) {
            options.maxFiles = 1;
        }
    }, [options]);

    const _onSubmit = () => {
        onSubmit && onSubmit(options.ownerId, currentFiles);
    };

    const _onCancel = () => onCancel && onCancel();

    const onFilesChanged = (fileArray: File[]) => {
        const files = fileArray.filter((file: File) => file.size <= maxFileSize).slice(0, options.maxFiles);
        if (files && files.length > 0) {
            set_canContinue(true);
        } else {
            set_canContinue(false);
        }
        set_currentFiles(files);
    };

    if (!(options.isOpen ?? false)) {
        return '';
    }

    return (
        <Dialog
            maxWidth='sm'
            open={true}
            onClose={() => _onCancel()}
            aria-labelledby='form-dialog-title'
        >
            <DialogContent>
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        role='dialog'
                        style={{ width: '550px' }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <div>
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1>File Upload</h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={() => _onCancel()}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                                <div>
                                    {options.fileTypeName && (
                                        <div style={{ padding: '10px 10px' }}>
                                            Type: <span style={{ fontWeight: 'bold' }}>{options.fileTypeName}</span>
                                        </div>
                                    )}
                                    <div style={{ padding: '10px 10px' }}>
                                        <ul>
                                            {options.maxFiles > 1 && (
                                                <li>
                                                    You may select up to <b>{options.maxFiles}</b> files. Any others beyond that
                                                    will NOT be uploaded.
                                                </li>
                                            )}
                                            {options.maxFiles === 1 && <li>Please select or drop only 1 file to upload</li>}
                                            <li>Only pdf, png, jpg and txt file types are allowed</li>
                                            <li>Maximum file size is 35 MB/document</li>
                                        </ul>
                                    </div>
                                    <div style={{ padding: '10px 10px' }}>
                                        <div
                                            id='drop_zone'
                                            style={styles.dropzone}
                                        >
                                            <FileUploader
                                                selectButtonText='Select Files'
                                                labelText='OR drag onto this panel...'
                                                uploadMode='useForm'
                                                maxFileSize={maxFileSize}
                                                invalidFileExtensionMessage='Invalid File Type. File will be ignored'
                                                invalidMaxFileSizeMessage='File too large. File will be ignored'
                                                multiple={options.maxFiles > 1}
                                                allowedFileExtensions={['.pdf', '.png', '.jpg', '.txt']}
                                                allowCanceling={true}
                                                onValueChange={onFilesChanged}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <DialogActions>
                                    <button
                                        style={styles.buttonMarginRight}
                                        type='button'
                                        className='button primary admin-slim-button'
                                        disabled={!canContinue}
                                        onClick={_onSubmit}
                                    >
                                        Continue
                                    </button>
                                    <button
                                        type='button'
                                        className='button hollow admin-slim-button'
                                        data-dismiss='modal'
                                        onClick={() => _onCancel()}
                                    >
                                        Cancel
                                    </button>
                                </DialogActions>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
